import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { getRestaurantMenu } from "../../../../lib/api/restaurant.apis";

interface PublicMenuData {
  restaurantName: string;
  logoUrl: string | null;
  menuPdfUrl: string;
}

export default function PublicMenuPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [data, setData] = useState<PublicMenuData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!restaurantId) return;
    const getMenuData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getRestaurantMenu(restaurantId);
            setData(data);
        } catch (error) {
            setError("Failed to load menu. refresh and try again.");
        } finally {
            setLoading(false);
        }
    };
    getMenuData();
  }, [restaurantId]);

  const adjustZoom = (delta: number) => {
    setZoom((z) => {
      const next = parseFloat((z + delta).toFixed(1));
      return Math.min(2, Math.max(0.5, next));
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-[#FFD166] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-gray-500">Loading menu…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center space-y-2">
          <p className="text-xl font-semibold text-gray-800">Menu unavailable</p>
          <p className="text-sm text-gray-500">
            {error ?? "This restaurant hasn't published a menu yet."}
          </p>
        </div>
      </div>
    );
  }

  const HEADER_H = 56;
  const TOOLBAR_H = 44;
  const viewerH = `calc(100vh - ${HEADER_H + TOOLBAR_H}px)`;

  return (
    <div className="flex flex-col min-h-screen bg-gray-900">

      {/* Slim header */}
      <header
        style={{ height: HEADER_H }}
        className="flex items-center gap-3 px-4 bg-white shadow-sm shrink-0"
      >
        {data.logoUrl && (
          <img
            src={data.logoUrl}
            alt="logo"
            className="w-9 h-9 rounded-lg object-cover border border-gray-100"
          />
        )}
        <span className="font-semibold text-gray-900 text-sm truncate">
          {data.restaurantName}
        </span>
        <span className="ml-auto text-xs text-gray-400">Menu</span>
      </header>

      {/* Zoom / size controls */}
      <div
        style={{ height: TOOLBAR_H }}
        className="flex items-center justify-center gap-2 px-4 bg-gray-100 border-b border-gray-200 shrink-0"
      >
        <button
          onClick={() => adjustZoom(-0.1)}
          disabled={zoom <= 0.5}
          title="Zoom out"
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors"
        >
          <Minus className="w-4 h-4" />
        </button>

        <input
          type="range"
          min={50}
          max={200}
          step={10}
          value={Math.round(zoom * 100)}
          onChange={(e) => setZoom(Number(e.target.value) / 100)}
          className="w-28 sm:w-36 accent-[#FFD166]"
        />

        <button
          onClick={() => adjustZoom(0.1)}
          disabled={zoom >= 2}
          title="Zoom in"
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>

        <span className="text-xs font-medium text-gray-600 w-10 text-center tabular-nums">
          {Math.round(zoom * 100)}%
        </span>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {[1, 1.5, 2].map((preset) => (
          <button
            key={preset}
            onClick={() => setZoom(preset)}
            className={`px-2.5 h-8 rounded-lg text-xs font-medium border shadow-sm transition-colors
              ${zoom === preset
                ? "bg-[#FFD166] border-[#FFD166] text-gray-900"
                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
          >
            {preset === 1 ? "Fit" : `${preset * 100}%`}
          </button>
        ))}

        <div className="w-px h-5 bg-gray-300 mx-1" />

        <button
          onClick={() => setZoom(1)}
          title="Reset to 100%"
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 shadow-sm transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      {/* PDF viewer — outer div is the scrollable viewport */}
      <div
        className="overflow-auto bg-gray-800"
        style={{ height: viewerH }}
      >
        <div
          style={{
            width: `${zoom * 100}%`,
            minWidth: "100%",
            height: `${zoom * 100}%`,
            minHeight: "100%",
            transition: "width 0.15s ease, height 0.15s ease",
          }}
        >
          <iframe
            key={zoom}
            src={`${data.menuPdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            title={`${data.restaurantName} Menu`}
            className="w-full h-full border-none"
          />
        </div>
      </div>
    </div>
  );
}